import { generateBridgeKey } from '@/crypto';
import { transport } from '@/api/transport';
import type { BridgeMode } from '@/api/types';
import { bridgePath } from '@/lib/links';
import { setOwnerToken, setPurpose } from '@/lib/storage';

import { sealFile, sealText } from './outgoing';

/** What the person came to do: the two tiles on the first screen and the receive plate under them. */
export type Kind = 'password' | 'chat' | 'receive';

/** The person stopped a file's upload: the bridge goes, the form comes back as it was, no error. */
export class UploadCancelled extends Error {}

export interface CreateBridgeInput {
  readonly kind: Kind;
  /** The text to seal (a `password` bridge); empty for none. */
  readonly secretText: string;
  readonly file: File | null;
  readonly ttl: number;
  readonly views: number;
  /** Called when a step of the guide is done: 1 after the key and the sealing, 2 after the server has it. */
  readonly onStep: (step: number) => void;
  readonly onProgress: (fraction: number) => void;
  /** Read through a function: it changes while the upload runs, when the person presses the plate's ×. */
  readonly isCancelled: () => boolean;
  /** No pauses between the steps: nobody is watching the guide (the Mac's quick-send shortcut). */
  readonly immediate?: boolean;
}

const pause = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/** Each step of creation ticks in the guide long enough to be seen, unless the viewer prefers reduced motion. */
function stepPause(): number {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 80 : 450;
}

/**
 * Makes the key, opens the bridge, seals the secret on this device, then has the server store the ciphertext. Resolves to the path of
 * the new bridge, key in the fragment. A bridge left without its secret is taken down again before the error leaves.
 */
export async function createBridge(input: CreateBridgeInput): Promise<string> {
  const { kind, secretText, file, ttl, views } = input;
  const wait = input.immediate === true ? 0 : stepPause();
  let created: { bridgeId: string; ownerToken: string } | null = null;
  try {
    // Step 1: the key is made on this device and the bridge opened on the server; what is sent is bound to the bridge's
    // id, so the secret is sealed once the bridge exists. A file gets its encrypted header now; its chunks are sealed
    // one by one on the way out.
    let started = Date.now();
    const { key, encoded } = await generateBridgeKey();
    const mode: BridgeMode = kind === 'password' ? 'secret' : 'chat';
    created = await transport.createBridge(ttl, views, mode);
    setOwnerToken(created.bridgeId, created.ownerToken);
    const writer = { bridgeId: created.bridgeId, from: 'owner' } as const;
    const secret = kind === 'password' && secretText.trim().length > 0 ? await sealText(key, secretText, writer) : null;
    const sealed = kind === 'password' && file ? await sealFile(key, file, writer) : null;
    await pause(Math.max(0, wait - (Date.now() - started)));
    input.onStep(1);
    // Step 2: the server stores ciphertext only.
    started = Date.now();
    if (secret !== null) await transport.postMessage(created.bridgeId, created.ownerToken, secret);
    if (sealed !== null) {
      if (input.isCancelled()) throw new UploadCancelled();
      await transport.postFile(created.bridgeId, created.ownerToken, sealed, (fraction) => {
        if (input.isCancelled()) throw new UploadCancelled();
        input.onProgress(fraction);
      });
    }
    if (kind === 'chat' || kind === 'receive') setPurpose(created.bridgeId, kind);
    await pause(Math.max(0, wait - (Date.now() - started)));
    input.onStep(2);
    return bridgePath(created.bridgeId, encoded);
  } catch (cause) {
    if (created !== null) {
      setOwnerToken(created.bridgeId, null);
      void transport.deleteBridge(created.bridgeId, created.ownerToken).catch(() => undefined);
    }
    throw cause;
  }
}
